import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { useGetMapConfig, useUpdateMapConfig } from '../services/appService';

function MapConfigFormModal({ open, onClose, appId }) {
    const [config, setConfig] = useState({
        initial_latitude: '',
        initial_longitude: '',
        initial_latitude_delta: '',
        initial_longitude_delta: '',
        map_filters: [],
    });
    const [newFilter, setNewFilter] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const getConfigHook = useGetMapConfig();
    const updateConfigHook = useUpdateMapConfig();

    useEffect(() => {
        const fetchConfig = async () => {
            if (open && appId) {
                setLoading(true);
                setError('');
                try {
                    const data = await getConfigHook(appId);
                    setConfig({
                        initial_latitude: data.initial_latitude?.toString() ?? '',
                        initial_longitude: data.initial_longitude?.toString() ?? '',
                        initial_latitude_delta: data.initial_latitude_delta?.toString() ?? '',
                        initial_longitude_delta: data.initial_longitude_delta?.toString() ?? '',
                        map_filters: data.map_filters || [],
                    });
                } catch (err) {
                    console.error('Error fetching map config:', err);
                    setError(err.message || 'Failed to load map configuration.');
                }
                 finally {
                    setLoading(false);
                }
            }
        };
        fetchConfig();
    }, [open, appId, getConfigHook]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setConfig(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleAddFilter = () => {
        if (newFilter && !config.map_filters.includes(newFilter)) {
            setConfig(prev => ({
                ...prev,
                map_filters: [...prev.map_filters, newFilter]
            }));
            setNewFilter('');
        }
    };

    const handleDeleteFilter = (filterToDelete) => () => {
        setConfig(prev => ({
            ...prev,
            map_filters: prev.map_filters.filter((filter) => filter !== filterToDelete),
        }));
    };

    const handleFilterInputChange = (event) => {
        setNewFilter(event.target.value);
    };

    const handleFilterInputKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault(); // Prevent form submission on Enter
            handleAddFilter();
        }
    };

    const handleSubmit = async () => {
        setError('');
        setSaving(true);

        // --- Basic Validation --- 
        const lat = parseFloat(config.initial_latitude);
        const lon = parseFloat(config.initial_longitude);
        const latDelta = parseFloat(config.initial_latitude_delta);
        const lonDelta = parseFloat(config.initial_longitude_delta);

        if (isNaN(lat) || isNaN(lon) || isNaN(latDelta) || isNaN(lonDelta)) {
            setError('Latitude, Longitude, and Deltas must be valid numbers.');
            setSaving(false);
            return;
        }
        if (latDelta <= 0 || lonDelta <= 0) {
             setError('Latitude Delta and Longitude Delta must be positive numbers.');
             setSaving(false);
             return;
        }
        if (config.map_filters.length === 0) {
            setError('At least one map filter is required.');
            setSaving(false);
            return;
        }
        // Add range validation if needed (e.g., lat between -90 and 90)

        const payload = {
            initial_latitude: lat,
            initial_longitude: lon,
            initial_latitude_delta: latDelta,
            initial_longitude_delta: lonDelta,
            map_filters: config.map_filters,
        };

        try {
            await updateConfigHook(appId, payload);
            onClose(); // Close modal on successful save
        } catch (err) {
            console.error('Error updating map config:', err);
            setError(err.message || 'Failed to save map configuration.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Edit Map Configuration</DialogTitle>
            <DialogContent>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box component="form" noValidate autoComplete="off">
                        <TextField
                            margin="dense"
                            name="initial_latitude"
                            label="Initial Latitude"
                            type="number"
                            fullWidth
                            variant="outlined"
                            value={config.initial_latitude}
                            onChange={handleChange}
                            required
                            inputProps={{ step: "any" }}
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            margin="dense"
                            name="initial_longitude"
                            label="Initial Longitude"
                            type="number"
                            fullWidth
                            variant="outlined"
                            value={config.initial_longitude}
                            onChange={handleChange}
                            required
                            inputProps={{ step: "any" }}
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            margin="dense"
                            name="initial_latitude_delta"
                            label="Initial Latitude Delta (Zoom Level)"
                            type="number"
                            fullWidth
                            variant="outlined"
                            value={config.initial_latitude_delta}
                            onChange={handleChange}
                            required
                            inputProps={{ step: "any", min: "0.0001" }} // Ensure positive
                             helperText="Smaller values mean more zoomed in"
                             sx={{ mb: 2 }}
                        />
                        <TextField
                            margin="dense"
                            name="initial_longitude_delta"
                            label="Initial Longitude Delta (Zoom Level)"
                            type="number"
                            fullWidth
                            variant="outlined"
                            value={config.initial_longitude_delta}
                            onChange={handleChange}
                            required
                            inputProps={{ step: "any", min: "0.0001" }} // Ensure positive
                            helperText="Smaller values mean more zoomed in"
                            sx={{ mb: 2 }}
                        />

                        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                            Map Filters
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                            {config.map_filters.map((filter) => (
                                <Chip
                                    key={filter}
                                    label={filter}
                                    onDelete={handleDeleteFilter(filter)}
                                />
                            ))}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TextField
                                margin="dense"
                                name="newFilter"
                                label="Add New Filter"
                                type="text"
                                variant="outlined"
                                value={newFilter}
                                onChange={handleFilterInputChange}
                                onKeyDown={handleFilterInputKeyDown}
                                size="small"
                            />
                            <Button onClick={handleAddFilter} variant="outlined" size="small">
                                Add
                            </Button>
                        </Box>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" disabled={loading || saving}>
                    {saving ? <CircularProgress size={24} /> : 'Save Configuration'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default MapConfigFormModal; 